/**
 * useCompositeState Hook
 *
 * Manages state for CompositeDataPicker:
 * - Routes incoming paths to appropriate pickers
 * - Aggregates loading states
 * - Merges selection changes back to single array
 */

import { useState, useEffect, useMemo, useCallback, useReducer, useRef } from 'react';
import type { Audience, Task } from '@segmently/schema-models/entities';
import type { FunnelVariable } from '@segmently/schema-models/v2';
import type { LoadingState } from '../../shared/types';
import { routePaths } from '../../shared/pathRouter';
import type { CompositeLoadingState, HydratedData } from './types';
import type { SocialProofHydratedData } from '../SocialProofFieldSelector/types';
import type { InsightsHydratedData } from '../InsightsFieldSelector/types';

interface UseCompositeStateProps {
  savedSelections: string[];
  onSelectionChange: (paths: string[]) => void;
  onLoadingChange?: (state: CompositeLoadingState) => void;
  onHydratedDataChange?: (data: HydratedData) => void;
}

interface UseCompositeStateResult {
  audiencePaths: string[];
  taskPaths: string[];
  variablePaths: string[];
  insightVariablePaths: string[];
  socialProofPaths: string[];
  insightsPaths: string[];
  invalidPaths: Array<{ path: string; reason: string }>;
  handleAudienceChange: (paths: string[]) => void;
  handleTaskChange: (paths: string[]) => void;
  handleVariableChange: (paths: string[]) => void;
  handleInsightVariableChange: (paths: string[]) => void;
  handleSocialProofChange: (paths: string[]) => void;
  handleInsightsChange: (paths: string[]) => void;
  handleAudienceLoadingChange: (state: LoadingState) => void;
  handleTaskLoadingChange: (state: LoadingState) => void;
  handleAudienceHydratedDataChange: (data: { audiences: Partial<Audience>[] }) => void;
  handleTaskHydratedDataChange: (data: Partial<Task>[]) => void;
  handleVariableHydratedDataChange: (data: FunnelVariable[]) => void;
  handleSocialProofHydratedDataChange: (data: SocialProofHydratedData) => void;
  handleInsightsHydratedDataChange: (data: InsightsHydratedData) => void;
}

/**
 * The six selection groups the composite picker owns.
 */
type SelectionGroup = 'audiences' | 'tasks' | 'variables' | 'insightVariables' | 'socialProofs' | 'insights';

/**
 * Single source of truth for the composite selection.
 *
 * All six groups live in ONE state object so that several pickers changing
 * inside the same React commit produce ONE consistent merged selection.
 * Previously each group had its own useState plus a render-time ref, and the
 * last handler of a commit emitted the other groups' stale (pre-commit) paths.
 */
interface SelectionState {
  audiences: string[];
  tasks: string[];
  variables: string[];
  /** Task 9: accepted insight-derived variables (`@insightVariable[...]`). */
  insightVariables: string[];
  socialProofs: string[];
  insights: string[];
  /**
   * Bumped by every `set`. The emission effect compares it against the last
   * epoch it emitted, so "there is something to emit" travels WITH the state
   * instead of in a ref that a still-pending dispatch cannot influence.
   * A plain ref flag could be consumed by an effect run that predates the
   * dispatch that armed it (children's passive effects flush before the
   * parent's, so a child picker's onChange lands mid-flush) — that change
   * would then never be emitted at all.
   */
  emitEpoch: number;
}

type SelectionGroups = Omit<SelectionState, 'emitEpoch'>;

type SelectionAction =
  | { type: 'set'; group: SelectionGroup; paths: string[] }
  | { type: 'replace'; selection: SelectionGroups };

const EMPTY_SELECTION: SelectionState = {
  audiences: [],
  tasks: [],
  variables: [],
  insightVariables: [],
  socialProofs: [],
  insights: [],
  emitEpoch: 0,
};

const SELECTION_GROUPS: SelectionGroup[] = [
  'audiences',
  'tasks',
  'variables',
  'insightVariables',
  'socialProofs',
  'insights',
];

function samePaths(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((path, index) => path === b[index]);
}

function selectionReducer(state: SelectionState, action: SelectionAction): SelectionState {
  switch (action.type) {
    case 'set':
      // Always a fresh object with a bumped epoch: the emission effect is keyed
      // on this state and must emit for every handler call, even a no-op
      // re-selection.
      return { ...state, [action.group]: action.paths, emitEpoch: state.emitEpoch + 1 };
    case 'replace':
      // External sync: never bumps the epoch, so it never emits.
      // Bail out when nothing actually changed, so a caller that passes a
      // fresh-identity savedSelections array on every render does not produce
      // a new state object (and a wasted re-render) each time.
      return SELECTION_GROUPS.every((group) => samePaths(state[group], action.selection[group]))
        ? state
        : { ...action.selection, emitEpoch: state.emitEpoch };
    default:
      return state;
  }
}

/**
 * Flattens the selection into the single array the parent consumes.
 */
function mergeSelection(selection: SelectionGroups): string[] {
  return [
    ...selection.audiences,
    ...selection.tasks,
    ...selection.variables,
    ...selection.socialProofs,
    ...selection.insights,
    // Appended LAST so the emitted order of the pre-existing groups never moves.
    ...selection.insightVariables,
  ];
}

/**
 * Custom hook for managing composite picker state
 */
export function useCompositeState({
  savedSelections,
  onSelectionChange,
  onLoadingChange,
  onHydratedDataChange,
}: UseCompositeStateProps): UseCompositeStateResult {
  // Route incoming paths (derived state, computed synchronously)
  const routedPaths = useMemo(() => routePaths(savedSelections), [savedSelections]);

  // Track internal vs external changes to prevent round-trip overwrites.
  // The emission effect sets this flag right before calling onSelectionChange.
  // The sync useEffect checks the flag and skips exactly one savedSelections
  // update, so the orchestrator round-trip cannot overwrite handler state.
  const isInternalChangeRef = useRef(false);

  // Selection for every picker in ONE reducer state (initially empty, filled by
  // the external sync effect below or by the picker handlers).
  const [selection, dispatchSelection] = useReducer(selectionReducer, EMPTY_SELECTION);

  // Last epoch handed to onSelectionChange. Only an effect run that actually
  // observes a bumped epoch emits, so an emission can never be "used up" by a
  // run that predates the dispatch that armed it, and React 18 StrictMode's
  // double-invoked effects cannot emit (or arm isInternalChangeRef) twice.
  const lastEmittedEpochRef = useRef(EMPTY_SELECTION.emitEpoch);

  // Hydrated data states
  const [audienceHydratedData, setAudienceHydratedData] = useState<Partial<Audience>[]>([]);
  const [taskHydratedData, setTaskHydratedData] = useState<Partial<Task>[]>([]);
  const [variableHydratedData, setVariableHydratedData] = useState<FunnelVariable[]>([]);
  const [socialProofHydratedData, setSocialProofHydratedData] = useState<SocialProofHydratedData>({
    reviews: [],
    ratings: [],
    studyCases: [],
    bigNumbers: [],
  });
  const [insightsHydratedData, setInsightsHydratedData] = useState<InsightsHydratedData>({
    analyses: [],
  });

  // Loading states
  const [audienceLoading, setAudienceLoading] = useState<LoadingState>({
    isLoading: true,
    loaded: 0,
    total: 0,
    currentItem: 'Initializing...',
  });

  const [taskLoading, setTaskLoading] = useState<LoadingState>({
    isLoading: true,
    loaded: 0,
    total: 0,
    currentItem: 'Initializing...',
  });

  // Initialize internal state when savedSelections change from external source.
  // Skips when the change originated from an internal handler (selectAll, user click)
  // to prevent the orchestrator round-trip from overwriting handler state.
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }
    dispatchSelection({
      type: 'replace',
      selection: {
        audiences: routedPaths.audiences,
        tasks: routedPaths.tasks,
        variables: routedPaths.variables,
        insightVariables: routedPaths.insightVariables,
        socialProofs: routedPaths.socialProofs,
        insights: routedPaths.insights,
      },
    });
    // `replace` never bumps emitEpoch: external sync must not echo back
    // through onSelectionChange (that would cause circular updates).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSelections]);

  // Single emission point: one merged selection per commit, built from the
  // reducer state (never from render-time refs), so several pickers changing
  // in the same commit still emit every group.
  useEffect(() => {
    if (selection.emitEpoch === lastEmittedEpochRef.current) return;
    lastEmittedEpochRef.current = selection.emitEpoch;
    // Tell the sync effect to skip exactly one round-trip echo of what we emit.
    isInternalChangeRef.current = true;
    onSelectionChange(mergeSelection(selection));
  }, [selection, onSelectionChange]);

  // Aggregate loading states and notify parent
  useEffect(() => {
    const compositeLoading: CompositeLoadingState = {
      isLoading: audienceLoading.isLoading || taskLoading.isLoading,
      pickers: {
        audience: audienceLoading,
        task: taskLoading,
      },
      totalLoaded: audienceLoading.loaded + taskLoading.loaded,
      totalItems: audienceLoading.total + taskLoading.total,
      currentPicker: audienceLoading.isLoading
        ? 'audience'
        : taskLoading.isLoading
        ? 'task'
        : null,
    };

    onLoadingChange?.(compositeLoading);
  }, [audienceLoading, taskLoading, onLoadingChange]);

  // Aggregate hydrated data and notify parent
  useEffect(() => {
    if (onHydratedDataChange) {
      const hydratedData: HydratedData = {
        audiences: audienceHydratedData,
        tasks: taskHydratedData,
        variables: variableHydratedData.length > 0 ? variableHydratedData : undefined,
        reviews: socialProofHydratedData.reviews.length > 0 ? socialProofHydratedData.reviews : undefined,
        ratings: socialProofHydratedData.ratings.length > 0 ? socialProofHydratedData.ratings : undefined,
        studyCases: socialProofHydratedData.studyCases.length > 0 ? socialProofHydratedData.studyCases : undefined,
        bigNumbers: socialProofHydratedData.bigNumbers.length > 0 ? socialProofHydratedData.bigNumbers : undefined,
        // W13: also published when NOTHING is ticked — `available` (the
        // unfiltered universe) is exactly what a consumer needs in that state.
        insights: insightsHydratedData.analyses.length > 0 || (insightsHydratedData.available?.length ?? 0) > 0
          ? insightsHydratedData
          : undefined,
      };
      onHydratedDataChange(hydratedData);
    }
  }, [audienceHydratedData, taskHydratedData, variableHydratedData, socialProofHydratedData, insightsHydratedData, onHydratedDataChange]);

  // Handlers for picker changes.
  // Every handler only writes its OWN group into the shared reducer state; the
  // merge happens once, after the commit, from the reduced state. No refs,
  // therefore no stale reads when several pickers fire selectAll inside the
  // same commit, and the emit intent rides along in the state (emitEpoch).
  const setGroup = useCallback((group: SelectionGroup, paths: string[]) => {
    dispatchSelection({ type: 'set', group, paths });
  }, []);

  const handleAudienceChange = useCallback((newPaths: string[]) => {
    setGroup('audiences', newPaths);
  }, [setGroup]);

  const handleTaskChange = useCallback((newPaths: string[]) => {
    setGroup('tasks', newPaths);
  }, [setGroup]);

  const handleVariableChange = useCallback((newPaths: string[]) => {
    setGroup('variables', newPaths);
  }, [setGroup]);

  const handleInsightVariableChange = useCallback((newPaths: string[]) => {
    setGroup('insightVariables', newPaths);
  }, [setGroup]);

  const handleSocialProofChange = useCallback((newPaths: string[]) => {
    setGroup('socialProofs', newPaths);
  }, [setGroup]);

  const handleInsightsChange = useCallback((newPaths: string[]) => {
    setGroup('insights', newPaths);
  }, [setGroup]);

  // Handlers for loading state changes
  const handleAudienceLoadingChange = useCallback((state: LoadingState) => {
    setAudienceLoading(state);
  }, []);

  const handleTaskLoadingChange = useCallback((state: LoadingState) => {
    setTaskLoading(state);
  }, []);

  // Handlers for hydrated data changes
  const handleAudienceHydratedDataChange = useCallback((data: { audiences: Partial<Audience>[] }) => {
    setAudienceHydratedData(data.audiences);
  }, []);

  const handleTaskHydratedDataChange = useCallback((data: Partial<Task>[]) => {
    setTaskHydratedData(data);
  }, []);

  const handleVariableHydratedDataChange = useCallback((data: FunnelVariable[]) => {
    setVariableHydratedData(data);
  }, []);

  const handleSocialProofHydratedDataChange = useCallback((data: SocialProofHydratedData) => {
    setSocialProofHydratedData(data);
  }, []);

  const handleInsightsHydratedDataChange = useCallback((data: InsightsHydratedData) => {
    setInsightsHydratedData(data);
  }, []);

  return {
    audiencePaths: selection.audiences,
    taskPaths: selection.tasks,
    variablePaths: selection.variables,
    insightVariablePaths: selection.insightVariables,
    socialProofPaths: selection.socialProofs,
    insightsPaths: selection.insights,
    invalidPaths: routedPaths.invalid,
    handleAudienceChange,
    handleTaskChange,
    handleVariableChange,
    handleInsightVariableChange,
    handleSocialProofChange,
    handleInsightsChange,
    handleAudienceLoadingChange,
    handleTaskLoadingChange,
    handleAudienceHydratedDataChange,
    handleTaskHydratedDataChange,
    handleVariableHydratedDataChange,
    handleSocialProofHydratedDataChange,
    handleInsightsHydratedDataChange,
  };
}
